import * as THREE from 'three';
import store from '../redux/store';
import { applyFogToScene } from './ThreeJsUtils/fogSceneUtils';
import { syncSceneVisibilityFromStore } from './ThreeJsUtils/assetVisibilityUtils';
import {
  applyLightVisibility,
  lightTypeHasHelper,
} from './ThreeJsUtils/lightHelperUtils';
import {
  getCameraHelperVisible,
  getCameraStableId,
  isSceneCamera,
  resolveSceneCameraObject,
} from './ThreeJsUtils/sceneCameraUtils';
import { handleHdriSelected } from './EnvironmentUtils';
import {
  hydrateSceneObjectEvents,
  isEventCapableSceneObject,
} from './eventAssetUtils';
import { UpdateMaterial } from '../components/gridpagefunction/materialFunction';
import { refreshAnimationTransformAnchors } from './ThreeJsUtils/syncObjectTransform';
import { rebuildAllTextsAfterHistoryRestore } from '../components/gridpagefunction/Text';
import { DownloadMesh } from '../components/gridpagefunction/Mesh';
import {
  syncSceneParticlePlaybackFromRedux,
  resolveParticleAnimationRoot,
} from './particles/particleAnimationUtils';
import {
  findDetachedSceneObjectByRecord,
  getGridHistoryEditorContext,
} from './gridHistory';
import {
  isLiveSceneObject3D,
  resolveLiveSceneObject,
} from './ThreeJsUtils/assetVisibilityUtils';
import {
  clearAllIntersectedObjects,
  restoreGridHistorySnapshot,
  setPushIntersectedObject,
  setSceneMeshes,
  selected_object,
} from '../redux/Slice/gridSlice';
import { suppressGridHistory } from './gridHistorySuppress';
import { syncAllMeshHierarchyFromRedux } from '../components/GridAssets/shapeAssetUtils';
import { clearParticleOutlineProxies } from './ThreeJsUtils/selectionOutline';
import {
  clampSelectionForChildMode,
  isGlbInnerMesh,
} from '../components/gridpagefunction/MeshExtractation';

function shouldSyncIntersectedPoolObject(scene, obj) {
  if (!obj?.isObject3D) return false;
  if (isGlbInnerMesh(scene, obj)) return false;
  if (obj.deleteType === 'mesh-extracted') return false;
  // Never put particle inner spheres/clusters into the selectable pool.
  if (resolveParticleAnimationRoot(obj) && obj.deleteType !== 'particle') {
    return false;
  }
  return !!(obj.deleteType || obj.isShapes || obj.userData?.type);
}

function normalizeRestoredEditorSelection(scene) {
  if (!scene) return;
  const selectObj = store.getState().assets_object?.select_object;
  if (!selectObj?.isObject3D) return;

  const particleRoot = resolveParticleAnimationRoot(selectObj);
  const clamped =
    particleRoot || clampSelectionForChildMode(scene, selectObj, null);
  if (clamped && clamped !== selectObj) {
    const endSuppress = suppressGridHistory();
    try {
      store.dispatch(selected_object(clamped));
    } finally {
      endSuppress();
    }
  }
}

function isLiveRootAsset(obj, scene) {
  if (!obj?.isObject3D || !scene) return false;
  return (
    obj.parent === scene ||
    (!obj.parent && scene.children.includes(obj))
  );
}

function syncReduxScenePoolsAfterRestore(scene) {
  if (!scene) return;

  const meshes = [];
  const intersected = new Set();
  const state = store.getState().assets_object;

  // Rebuild pools from the live scene graph only — snapshot pools can hold
  // detached/stale Object3D refs after undo/redo and break picks + visibility.
  scene.traverse((obj) => {
    if (!shouldSyncIntersectedPoolObject(scene, obj)) return;
    if (isGlbInnerMesh(scene, obj)) return;
    if (obj.userData?.isParticleOutlineProxy) return;
    intersected.add(obj);
    if (
      isLiveRootAsset(obj, scene) &&
      obj.deleteType === 'mesh' &&
      (obj.objType === '3d' ||
        obj.isShapes ||
        obj.glb_type === 'shapes' ||
        obj.userData?.glb_type === 'shapes')
    ) {
      meshes.push(obj);
    }
  });

  const filterLivePool = (pool) =>
    (pool || []).filter(
      (obj) =>
        obj?.isObject3D &&
        isObjectInSceneGraph(scene, obj) &&
        !obj.userData?.isParticleOutlineProxy
    );

  const endSuppress = suppressGridHistory();
  try {
    store.dispatch(setSceneMeshes(meshes));
    store.dispatch(clearAllIntersectedObjects());
    intersected.forEach((obj) => {
      store.dispatch(setPushIntersectedObject(obj));
    });

    // Drop stale live refs from other scene pools restored from the snapshot.
    const poolPatch = {
      sceneMeshes: meshes,
      sceneImages: filterLivePool(state.sceneImages),
      sceneVideos: filterLivePool(state.sceneVideos),
      sceneAudios: filterLivePool(state.sceneAudios),
      sceneText: filterLivePool(state.sceneText),
      sceneHotspots: filterLivePool(state.sceneHotspots),
      sceneElements: filterLivePool(state.sceneElements),
      allIntersectedObjects: [...intersected],
    };
    store.dispatch(restoreGridHistorySnapshot({ ...poolPatch }));
  } finally {
    endSuppress();
  }
}

function isObjectInSceneGraph(scene, obj) {
  if (!scene || !obj) return false;
  let found = false;
  scene.traverse((child) => {
    if (child === obj) found = true;
  });
  return found;
}

function attachRootAssetToScene(scene, obj) {
  if (!scene || !obj?.isObject3D) return false;
  if (obj.parent === scene) return true;
  obj.parent?.remove?.(obj);
  scene.add(obj);
  return true;
}

function reattachMissingAssetsAfterHistoryRestore(scene) {
  if (!scene) return [];

  const attached = [];
  const seen = new Set();
  const state = store.getState().assets_object;
  const assetsData = state.assets_data || {};

  const tryAttach = (obj) => {
    if (!isLiveSceneObject3D(obj) || seen.has(obj.uuid)) return;
    if (isObjectInSceneGraph(scene, obj)) return;
    if (!obj.deleteType && !obj.isShapes && !obj.isCamera && !obj.isLight) {
      return;
    }
    if (!attachRootAssetToScene(scene, obj)) return;
    seen.add(obj.uuid);
    attached.push(obj);
  };

  [
    ...(state.sceneMeshes || []),
    ...(state.sceneImages || []),
    ...(state.sceneVideos || []),
    ...(state.sceneAudios || []),
    ...(state.sceneText || []),
    ...(state.sceneHotspots || []),
    ...(state.allIntersectedObjects || []),
  ].forEach(tryAttach);

  ['mesh', 'image', 'video', 'audio', 'text', 'hotspot', 'empty'].forEach((type) => {
    (assetsData[type] || []).forEach((record) => {
      const live = resolveLiveSceneObject(scene, record, type);
      if (live && isObjectInSceneGraph(scene, live)) return;

      const detached = findDetachedSceneObjectByRecord(record);
      if (detached) {
        tryAttach(detached);
      }
    });
  });

  if (!attached.length) return attached;

  const endSuppress = suppressGridHistory();
  try {
    attached.forEach((obj) => {
      store.dispatch(setPushIntersectedObject(obj));
    });
  } finally {
    endSuppress();
  }

  return attached;
}

async function rebuildMissingMeshesAfterHistoryRestore(scene) {
  const editorContext = getGridHistoryEditorContext();
  if (!scene || !editorContext?.GLTFLoader || !editorContext?.Three) return;

  const meshes = store.getState().assets_object?.assets_data?.mesh || [];
  const missing = meshes.filter(
    (record) => !resolveLiveSceneObject(scene, record, 'mesh')
  );
  if (!missing.length) return;

  const publishable = missing.filter((record) => record?.asset_file_path);
  if (!publishable.length) return;

  DownloadMesh(publishable, editorContext);
  await new Promise((resolve) => setTimeout(resolve, 1200));
}

function applyBackgroundFromEnvironment(scene, environmentData) {
  if (!scene || !environmentData) return;

  const isSkyboxColor = environmentData.skybox?.name === 'Color';
  const colorConfig = environmentData.color_config;
  const colorType = colorConfig?.color_type;

  if (!isSkyboxColor || !colorConfig || !colorType || colorType === 'None') {
    return;
  }

  if (colorType === 'Single' && colorConfig.color) {
    scene.background = new THREE.Color(colorConfig.color);
    return;
  }

  if (
    colorType === 'Gradient' &&
    colorConfig.top_color &&
    colorConfig.bottom_color
  ) {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, colorConfig.top_color);
    gradient.addColorStop(1, colorConfig.bottom_color);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    scene.background = new THREE.CanvasTexture(canvas);
  }
}

function syncEnvironmentFromRedux(scene) {
  const environmentData = store.getState().environment?.environmentData;
  if (!environmentData || !scene) return;

  applyFogToScene(scene, environmentData);

  const skyboxName = environmentData.skybox?.name;
  if (skyboxName === 'HDRI' && environmentData.hdriFile) {
    handleHdriSelected(
      null,
      environmentData.hdriFile,
      Number(environmentData.hdriRotation) || 0,
      scene,
      null,
      environmentData.hdriVisible !== false,
      null,
      Number(environmentData.hdriLightIntensity) || 1
    );
    return;
  }

  applyBackgroundFromEnvironment(scene, environmentData);
}

function syncLightsFromRedux(scene) {
  const lightData = store.getState().assets_object?.light_data || [];

  lightData.forEach((light) => {
    const sceneLight = scene.getObjectByName(light.name);
    if (!sceneLight?.isLight) return;

    if (light.position) {
      sceneLight.position.set(
        light.position.x ?? 0,
        light.position.y ?? 0,
        light.position.z ?? 0
      );
    }
    if (light.rotation) {
      sceneLight.rotation.set(
        light.rotation.x ?? 0,
        light.rotation.y ?? 0,
        light.rotation.z ?? 0
      );
    }
    if (light.color && sceneLight.color?.set) {
      sceneLight.color.set(light.color);
    }
    if (typeof light.intensity !== 'undefined') {
      sceneLight.intensity = light.intensity;
    }
    if (typeof light.range !== 'undefined' && 'distance' in sceneLight) {
      sceneLight.distance = light.range;
    }
    if (typeof light.angle !== 'undefined' && 'angle' in sceneLight) {
      sceneLight.angle = light.angle;
    }
    if (typeof light.width !== 'undefined' && 'width' in sceneLight) {
      sceneLight.width = light.width;
    }
    if (typeof light.height !== 'undefined' && 'height' in sceneLight) {
      sceneLight.height = light.height;
    }

    applyLightVisibility(
      sceneLight,
      { ...light, id: light.id || light.name },
      scene,
      { isDesignMode: true }
    );

    const helperId = `${light.id || light.name}-helper`;
    const helper = scene.getObjectByName(helperId);
    if (helper && typeof helper.update === 'function') {
      helper.visible =
        light.visible !== false &&
        light.helperVisible === true &&
        lightTypeHasHelper(light.light_type);
      helper.update();
    }
  });
}

function syncCamerasFromRedux(scene) {
  const cameraProperties =
    store.getState().assets_object?.camera_properties || [];

  cameraProperties.forEach((camProp) => {
    const cam = resolveSceneCameraObject(scene, camProp);
    if (!isSceneCamera(cam)) return;

    if (camProp.position) {
      cam.position.set(
        camProp.position.x ?? 0,
        camProp.position.y ?? 0,
        camProp.position.z ?? 0
      );
    }
    if (camProp.rotation) {
      cam.rotation.set(
        camProp.rotation.x ?? 0,
        camProp.rotation.y ?? 0,
        camProp.rotation.z ?? 0
      );
    }

    if (cam.isPerspectiveCamera) {
      if (camProp.fov != null) cam.fov = Number(camProp.fov);
      if (camProp.near != null) cam.near = Number(camProp.near);
      if (camProp.far != null) cam.far = Number(camProp.far);
      cam.updateProjectionMatrix();
    } else if (cam.isOrthographicCamera) {
      if (camProp.near != null) cam.near = Number(camProp.near);
      if (camProp.far != null) cam.far = Number(camProp.far);
      if (camProp.size != null) {
        const half = Number(camProp.size) / 2;
        cam.top = half;
        cam.bottom = -half;
        const aspect = cam.right - cam.left || 1;
        cam.left = -half * aspect;
        cam.right = half * aspect;
      }
      cam.updateProjectionMatrix();
    }

    const stableId = getCameraStableId(camProp);
    const helperVisible = getCameraHelperVisible(camProp);
    const helper =
      scene.getObjectByName(`${stableId}Helper`) ||
      scene.getObjectByName(`${cam.name}Helper`);
    // Never force-create helpers here — only update visibility when one exists.
    if (helper) {
      helper.visible = helperVisible;
      if (helperVisible && typeof helper.update === 'function') helper.update();
    }

    const point =
      scene.getObjectByName(`${stableId}Point`) ||
      scene.getObjectByName(`${cam.name}Point`);
    if (point) point.position.copy(cam.position);
  });
}

function syncEventsFromRedux(scene) {
  const assetsData = store.getState().assets_object?.assets_data || {};
  scene.traverse((obj) => {
    if (isEventCapableSceneObject(obj)) {
      hydrateSceneObjectEvents(obj, assetsData);
    }
  });
}

function syncAssetMaterialsFromUserData(scene) {
  scene.traverse((obj) => {
    const settings = obj.userData?.material_settings;
    if (!settings || !obj.isMesh) return;
    try {
      UpdateMaterial({ object: obj, addData: settings });
    } catch (error) {
      console.warn('Material restore skipped for', obj.name, error);
    }
  });
}

function requestSceneRender(scene) {
  window.dispatchEvent(
    new CustomEvent('gridHistoryRestored', { detail: { scene } })
  );
}

export async function syncSceneAfterGridHistoryRestore(scene) {
  if (!scene) return;

  clearParticleOutlineProxies();
  reattachMissingAssetsAfterHistoryRestore(scene);
  await rebuildMissingMeshesAfterHistoryRestore(scene);
  syncReduxScenePoolsAfterRestore(scene);
  syncAllMeshHierarchyFromRedux(scene);
  normalizeRestoredEditorSelection(scene);

  syncSceneVisibilityFromStore(scene);
  syncLightsFromRedux(scene);
  syncCamerasFromRedux(scene);
  syncEnvironmentFromRedux(scene);
  syncEventsFromRedux(scene);
  syncAssetMaterialsFromUserData(scene);

  const selectedObject = store.getState().assets_object?.select_object;
  await rebuildAllTextsAfterHistoryRestore(scene, {
    scene,
    selectedObject,
  });
  syncSceneParticlePlaybackFromRedux(scene);

  // Re-bind animation frame/original vectors as live Three.js objects.
  scene.traverse((obj) => {
    if (obj?.userData && (obj.deleteType || obj.isMesh || obj.isGroup)) {
      refreshAnimationTransformAnchors(obj);
    }
  });

  requestSceneRender(scene);
}
